import { FC, useCallback, useEffect, useRef, useState } from 'react';
import Modal from '@components/Modal';
import { useQuestionnaireContext, useQuestionnaireFormContext } from '@/components/Questionnaire/QuestionnaireProvider';
import Buttons from '@components/Buttons';
import Spacer from '@components/Spacer';
import RadioInput from '@components/FormFields/RadioInputCards';
import type { Questionnaire, FieldType } from '@/typings/Questionnaire';
import {
  AddressIntl,
  AddressUS,
  AddressUSOnBlur,
  Checkboxes,
  Currency,
  CurrencyOnBlur,
  DatePicker,
  DatePickerOnBlur,
  InputOnBlur,
  TextAreaOnBlur,
  PhoneInput,
  Percent,
  RadioButtons,
  Select,
  NameOnBlur,
  NumberInput,
  NumberInputOnBlur,
  Input,
  Name,
  TextArea,
} from '@/components/DesignLibrary/Atoms';
import Label from '@/components/DesignLibrary/Atoms/shared/Label';
import MedianIncomeWidget from '@/components/Questionnaire/form/MedianIncomeWidget';
import MonthlyIncomeRatio from '@/components/Questionnaire/form/MonthlyIncomeRatio';
import { Controller } from 'react-hook-form';
import getAllLinkedFieldKeys from '@helpers/getAllLinkedFieldKeys';
import { useFeatureFlag } from '@/providers/FeatureFlags';
import s from './EditQuestionnaireFieldModal.module.scss';
import CreditorSelect from '../CreditorSelect';

type Props = {
  field: Questionnaire.FieldCommon;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules: any;
  onClose: () => void;
  onSubmit: () => void;
};

type UpdateTypes = 'self' | 'allLinkedFields';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const componentsMap: { [key in Exclude<FieldType, 'list' | 'table'>]: React.FC<any> } = {
  'address-international': AddressIntl,
  'address-us': AddressUS,
  break: () => null,
  'creditor-select': CreditorSelect,
  currency: Currency,
  date: DatePicker,
  explanation: Label,
  'long-text': TextArea,
  'median-income': MedianIncomeWidget,
  'monthly-income': MonthlyIncomeRatio,
  'multi-select': Checkboxes,
  name: Name,
  number: NumberInput,
  numeric: Input,
  percent: Percent,
  'phone-number': PhoneInput,
  selection: Select,
  'short-text': Input,
  'single-select': RadioButtons,
  'ssn': Input,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const componentsOnBlurMap: { [key in Exclude<FieldType, 'list' | 'table'>]: React.FC<any> } = {
  'address-international': AddressIntl,
  'address-us': AddressUSOnBlur,
  break: () => null,
  'creditor-select': CreditorSelect,
  currency: CurrencyOnBlur,
  date: DatePickerOnBlur,
  explanation: Label,
  'long-text': TextAreaOnBlur,
  'median-income': MedianIncomeWidget,
  'monthly-income': MonthlyIncomeRatio,
  'multi-select': Checkboxes,
  name: NameOnBlur,
  number: NumberInputOnBlur,
  numeric: InputOnBlur,
  percent: Percent,
  'phone-number': PhoneInput,
  selection: Select,
  'short-text': InputOnBlur,
  'single-select': RadioButtons,
  'ssn': InputOnBlur,
};

const EditQuestionnaireFieldModal: FC<Props> = ({ onClose, onSubmit, field, control, rules }) => {
  const { fieldsById } = useQuestionnaireContext();
  const {
    formMethods,
    setValue,
  } = useQuestionnaireFormContext();

  const modalRef = useRef<HTMLDivElement>(null);
  const [isUpdatingField, setIsUpdatingField] = useState(false);
  const [internalValue, setInternalValue] = useState(formMethods.getValues(field.key));
  const [selectedUpdateType, setSelectedUpdateType] = useState<UpdateTypes>('allLinkedFields');
  const betaPerformanceImprovements = useFeatureFlag('beta-performance-improvements');

  const handleUpdateField = useCallback(async (): Promise<void> => {
    setIsUpdatingField(true);
    setValue({ key: field.key, source: 'manual', value: internalValue });
    if (field.linkedFields?.length && selectedUpdateType === 'allLinkedFields') {
      const fieldTemplateKeys = getAllLinkedFieldKeys({
        field,
        fieldsById,
      });
      // Update all associated keys with new value
      fieldTemplateKeys.forEach((key) => {
        setValue({ key, source: 'manual', value: internalValue });
      });
    }
    onSubmit();
  }, [field, fieldsById, internalValue, selectedUpdateType, onSubmit, setValue]);

  useEffect(() => {
    if (modalRef.current) {
      const input = modalRef.current.querySelector<HTMLInputElement>(
        'input[type="text"], input[type="short-text"], input[type="number"], input[type="currency"], textarea',
      );
      if (input) {
        input.focus();
      }
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = async (event: KeyboardEvent): Promise<void> => {
      if (event.key === 'Enter') {
        event.preventDefault();
        await handleUpdateField();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleUpdateField]);

  const handleChooseUpdateType = (type: UpdateTypes): void => {
    setSelectedUpdateType(type);
  };

  const getButtonText = (): string => selectedUpdateType === 'self' ? 'Update just here' : 'Update everywhere';

  if (field.type === 'list' || field.type === 'table') {
    return null;
  }

  const Component = betaPerformanceImprovements ? componentsOnBlurMap[field.type] : componentsMap[field.type];

  const { key, ...restProps } = field;

  return (
    <Modal
      hideBottomInset
      title={''}
      onClose={onClose}
      ref={modalRef}
    >
      <div className={s.editQFieldModalPage}>
        <span className={s.header}>This field is linked to other fields</span>
        <Spacer size={12}/>
        <span className={s.caption}>o you want to change it just here or all linked fields?</span>
        <Spacer size={24}/>
        <RadioInput
          inputName="updateType"
          inputs={[
            { id: 'self', label: 'Update just here' },
            { defaultChecked: true, id: 'allLinkedFields', label: 'Update everywhere' },
          ]}
          onChange={handleChooseUpdateType}
        />
        <Spacer size={24}/>
        <Controller
          name={field.key}
          control={control}
          shouldUnregister={false}
          rules={rules}
          render={({
            field: { onBlur, ref },
            fieldState: { error },
          }) => (
            <Component
              key={key}
              {...restProps}
              isPreview={false}
              triggerOnChangeOnBlur={!!betaPerformanceImprovements}
              field={field}
              required={field.isRequired}
              optional={!field.isRequired}
              disabled={field.disabled}
              value={internalValue}
              notInQuestionnaire={false}
              onChange={(v: typeof internalValue) => {
                const handledValue = field.type === 'single-select' ? v.target.value : v;
                setInternalValue(handledValue);
              }}
              onBlur={async () => {
                onBlur();
                if (error?.message) {
                  await formMethods.trigger();
                }
              }}
              control={control}
              ref={field.type === 'list' || field.type === 'single-select' ? null : ref}
              rules={rules}
              error={error?.message}
            />
          )}
        />
        <Spacer size={24}/>
        <Buttons
          isSecondary
          isFullWidth
          onClick={handleUpdateField}
          className={s.updateButton}
          isFetching={isUpdatingField}
        >
          {getButtonText()}
        </Buttons>
      </div>
    </Modal>
  );
};

export default EditQuestionnaireFieldModal;
